import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";

export default function Settings() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <h1 className="text-2xl font-heading font-bold">Healion.ai</h1>
            <nav className="hidden md:flex gap-6">
              <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground">
                Dashboard
              </Link>
              <Link to="/errors" className="text-sm text-muted-foreground hover:text-foreground">
                Errors
              </Link>
              <Link to="/connections" className="text-sm text-muted-foreground hover:text-foreground">
                Connections
              </Link>
              <Link to="/analytics" className="text-sm text-muted-foreground hover:text-foreground">
                Analytics
              </Link>
              <Link to="/settings" className="text-sm font-medium border-b-2 border-foreground pb-1">
                Settings
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-heading font-bold mb-2">Settings</h1>
        <p className="text-muted-foreground mb-8">Manage your account and preferences</p>

        <div className="space-y-6">
          {/* General Settings */}
          <Card className="p-6 border-2">
            <h2 className="text-xl font-heading font-bold mb-4">General</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="company">Company Name</Label>
                <Input id="company" defaultValue="Acme Inc." />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" defaultValue="admin@acme.com" />
              </div>
              <Button>Save Changes</Button>
            </div>
          </Card>

          {/* Auto-merge Settings */}
          <Card className="p-6 border-2">
            <h2 className="text-xl font-heading font-bold mb-4">Auto-merge</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Enable auto-merge</p>
                  <p className="text-sm text-muted-foreground">
                    Automatically merge fixes after approval
                  </p>
                </div>
                <Switch />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Require reviews</p>
                  <p className="text-sm text-muted-foreground">
                    Require team approval before auto-merge
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <div>
                <Label htmlFor="reviewers">Minimum reviewers</Label>
                <Input id="reviewers" type="number" defaultValue="1" className="w-32" />
              </div>
            </div>
          </Card>

          {/* Notifications */}
          <Card className="p-6 border-2">
            <h2 className="text-xl font-heading font-bold mb-4">Notifications</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Slack notifications</p>
                  <p className="text-sm text-muted-foreground">
                    Send fix updates to Slack
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email notifications</p>
                  <p className="text-sm text-muted-foreground">
                    Receive email updates
                  </p>
                </div>
                <Switch />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Weekly summary</p>
                  <p className="text-sm text-muted-foreground">
                    Weekly digest of activity
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
            </div>
          </Card>

          {/* Team */}
          <Card className="p-6 border-2">
            <h2 className="text-xl font-heading font-bold mb-4">Team</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                {[
                  { name: "Admin User", email: "admin@acme.com", role: "Owner" },
                  { name: "John Doe", email: "john@acme.com", role: "Admin" },
                  { name: "Jane Smith", email: "jane@acme.com", role: "Member" },
                ].map((member, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium text-sm">{member.name}</p>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 rounded-full bg-muted">
                        {member.role}
                      </span>
                      {member.role !== "Owner" && (
                        <Button variant="ghost" size="sm">Remove</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline">Invite Team Member</Button>
            </div>
          </Card>

          {/* Billing */}
          <Card className="p-6 border-2">
            <h2 className="text-xl font-heading font-bold mb-4">Billing</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
                <div>
                  <p className="font-medium">Pro Plan</p>
                  <p className="text-sm text-muted-foreground">$99/month</p>
                </div>
                <Button variant="outline">Manage Plan</Button>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Payment method</p>
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <span className="text-sm">•••• •••• •••• 4242</span>
                  <Button variant="ghost" size="sm">Update</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
